import { useEffect, useCallback } from 'react';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { makeHeader, makeRow, SectionTitle, StandardTable } from '@bento-dental/core';
import { OutlinedTextField, DatePickerField } from '@bento-dental/core/fields';
import { formatDate, parseDate } from '@bento-dental/core/utilities';
import { getRelationshipLabel } from '@bento-dental/core/members';
import { CobraElectFormState, CobraInfo, CobraFamilyMember, EnrollmentPlan } from './types';

interface CobraElectFormProps {
  patientId?: string | number | null;
  cobraInfo: CobraInfo | null;
  formState: CobraElectFormState;
  setFormState: (updates: Partial<CobraElectFormState>) => void;
}

function CobraElectForm({ patientId, cobraInfo, formState, setFormState }: CobraElectFormProps) {
  const { planYears, planYear, plans, family } = formState;

  const getEmployer = useCallback(() => {
    if (cobraInfo?.employers?.length) {
      setFormState({ employer: cobraInfo.employers[0].corp ?? null });
    }
  }, [cobraInfo, setFormState]);

  const getFamily = useCallback(() => {
    if (cobraInfo?.family_members) {
      setFormState({ family: cobraInfo.family_members });
    }
  }, [cobraInfo, setFormState]);

  const getPlanYears = useCallback(() => {
    if (!cobraInfo?.employers?.length) return;
    if (!formState.employer) return;
    const employer = cobraInfo.employers[0];
    const employerPlanYears = employer.plan_years ?? [];
    setFormState({ planYears: employerPlanYears });
    if (employerPlanYears.length === 1) {
      setFormState({ planYear: String(employerPlanYears[0].plan_year) });
    }
  }, [cobraInfo, formState.employer, setFormState]);

  const getPlans = useCallback(() => {
    if (!cobraInfo?.employers?.length) return;
    if (!formState.planYear) return;
    const employer = cobraInfo.employers[0];
    const employerPlanYears = employer.plan_years ?? [];
    const selectedPlanYear = employerPlanYears.find(
      (item) => String(item.plan_year) === String(formState.planYear)
    );
    if (!selectedPlanYear) {
      setFormState({ plans: [] });
      return;
    }
    setFormState({ plans: selectedPlanYear.plans });
    if (selectedPlanYear.plans.length === 1) {
      setFormState({
        plan: String(selectedPlanYear.plans[0].id),
        planType: selectedPlanYear.plans[0].type,
      });
    }
  }, [cobraInfo, formState.planYear, setFormState]);

  useEffect(() => {
    getEmployer();
    getFamily();
  }, [patientId]);

  useEffect(() => {
    if (formState.employer) {
      getPlanYears();
    }
  }, [formState.employer, getPlanYears]);

  useEffect(() => {
    if (formState.employer && formState.planYear) {
      getPlans();
    }
  }, [formState.employer, formState.planYear, getPlans]);

  const headers = makeHeader('covered-family', ['Elect', 'Name', 'Relationship', 'Status']);

  const rows = (family ?? []).map((familyMember: CobraFamilyMember) =>
    makeRow(`covered-family-${familyMember.id}`, [
      <Checkbox
        id={`covered-family-checkbox-${familyMember.patient}`}
        key={`covered-family-checkbox-${familyMember.patient}`}
        color="primary"
        checked={
          Boolean(familyMember.has_cobra) ||
          formState.familyPatientIdsToCover.includes(String(familyMember.patient))
        }
        value={String(familyMember.patient)}
        onChange={(event) => {
          const { familyPatientIdsToCover } = formState;
          const memberPatientId = String(event.target.value);
          const updatedIds = familyPatientIdsToCover.includes(memberPatientId)
            ? familyPatientIdsToCover.filter((id) => id !== memberPatientId)
            : [...familyPatientIdsToCover, memberPatientId];
          setFormState({ familyPatientIdsToCover: updatedIds, familyPatientIdsToCoverError: '' });
        }}
        disabled={!familyMember.is_eligible_for_cobra}
      />,
      familyMember.name,
      getRelationshipLabel(familyMember.relation),
      familyMember.cobra_elect_status,
    ])
  );

  const getProjectedExpirationDate = () => {
    const { effectiveDate, cobraExpirationPeriodMonths } = formState;
    if (!effectiveDate || !cobraExpirationPeriodMonths) {
      return null;
    }
    const endDate = dayjs(effectiveDate).add(cobraExpirationPeriodMonths, 'months');
    return formatDate(endDate);
  };

  const selectedPlanYear = (planYears || []).find((item) => item.plan_year === planYear);
  const effectiveDateSelectStartDate = selectedPlanYear?.coverage_start_date;
  const effectiveDateSelectEndDate = selectedPlanYear?.coverage_end_date;

  return (
    <Grid container>
      <Grid size={6}>
        <OutlinedTextField
          select
          id="plan-years"
          name="planYear"
          label="Plan Year"
          value={formState.planYear ?? ''}
          onChange={(event) => {
            const newPlanYear = event.target.value;
            setFormState({ planYear: String(newPlanYear), planYearError: '', plan: null });
          }}
          error={Boolean(formState.planYearError)}
          helperText={formState.planYearError}
          disabled={!formState.employer}
          InputLabelProps={{ shrink: Boolean(formState.employer) }}
        >
          {planYears?.map((option) => (
            <MenuItem
              id={`plan-year-${option.plan_year}`}
              key={`plan-year-${option.plan_year}`}
              value={String(option.plan_year)}
            >
              {option.label}
            </MenuItem>
          ))}
        </OutlinedTextField>
      </Grid>
      <Grid size={6}>
        <OutlinedTextField
          select
          id="plan"
          name="plan"
          label="Plan"
          value={formState.plan ?? ''}
          onChange={(event) => {
            const selectedPlanId = event.target.value;
            const selectedPlan = plans.find(
              (plan: EnrollmentPlan) => String(plan.id) === String(selectedPlanId)
            );
            if (selectedPlan) {
              setFormState({
                planType: selectedPlan.type,
                plan: String(selectedPlan.id),
                planError: '',
              });
            } else {
              setFormState({ plan: String(selectedPlanId), planError: '' });
            }
          }}
          error={Boolean(formState.planError)}
          helperText={formState.planError}
          disabled={!formState.planYear}
          InputLabelProps={{ shrink: Boolean(formState.planYear) }}
        >
          {plans.map((option) => (
            <MenuItem id={`plan-${option.id}`} key={`plan-${option.id}`} value={String(option.id)}>
              {option.label}
            </MenuItem>
          ))}
        </OutlinedTextField>
      </Grid>
      <Grid size={12}>
        <SectionTitle>Covered Family</SectionTitle>
      </Grid>
      <Grid size={12}>
        <Stack>
          <StandardTable id="covered-family" headers={headers} rows={rows} />
          {formState.familyPatientIdsToCoverError && (
            <Typography fontSize={12} marginLeft={1.8} marginTop={-1.5} color="error">
              Select at least one member
            </Typography>
          )}
        </Stack>
      </Grid>
      <Grid size={6}>
        <DatePickerField
          id="effective-date"
          name="effectiveDate"
          label="Effective Date"
          value={formState.effectiveDate ? dayjs(formState.effectiveDate) : null}
          minDate={effectiveDateSelectStartDate ? parseDate(effectiveDateSelectStartDate) : null}
          maxDate={effectiveDateSelectEndDate ? parseDate(effectiveDateSelectEndDate) : null}
          onChange={(event) => {
            setFormState({ effectiveDate: event.target.value, effectiveDateError: '' });
          }}
          error={Boolean(formState.effectiveDateError)}
          helperText={formState.effectiveDateError}
        />
      </Grid>
      <Grid size={6}>
        <OutlinedTextField
          select
          id="cobra-expiration-period"
          name="cobraExpirationPeriodMonths"
          label="COBRA Expiration"
          value={formState.cobraExpirationPeriodMonths ?? ''}
          onChange={(event) => {
            setFormState({
              cobraExpirationPeriodMonths: Number(event.target.value),
              cobraExpirationPeriodMonthsError: '',
            });
          }}
          error={Boolean(formState.cobraExpirationPeriodMonthsError)}
          helperText={formState.cobraExpirationPeriodMonthsError}
        >
          <MenuItem value={18}>18 Months</MenuItem>
          <MenuItem value={36}>36 Months</MenuItem>
        </OutlinedTextField>
      </Grid>
      {formState.effectiveDate && formState.cobraExpirationPeriodMonths && (
        <Grid size={12}>
          <Typography variant="subtitle1">
            {`COBRA coverage will expire automatically on ${getProjectedExpirationDate()}`}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
}

export default CobraElectForm;
